import React, { useEffect, useRef } from 'react';
import { CAROUSEL_LAYOUT } from 'ds/components/carousels/cards-carousel/BaseCardsCarousel';
import { useScreenBreakpoint, BreakpointValue } from 'consts/breakpoints';
import { ScrollSpyProvider, ScrollSpyTarget } from 'components/scroll-spy';
import { animated, useTrail } from 'react-spring';
import {
  ScrollXBoxContainer,
  ScrollXBox,
  ScrollXBoxWithCalculatedWidth,
  ScrollXBoxPaddingContainer,
  ScrollXBoxWrapper,
} from './styled';
import { noop } from 'lodash';
import { createPanResponder } from 'helpers/pan-responder';

export interface ICarouselLayout {
  cardsColumnsNumber: Record<BreakpointValue, number>;
  showNextSlideStart?: Partial<Record<BreakpointValue, boolean>>;
}

export interface ScrollBoxProps {
  autoWidth?: boolean;
  isAnimated?: boolean;
  cacheKey?: string | symbol;
  carouselLayout?: ICarouselLayout;
  excludedTail?: JSX.Element;
  padding?: string;
  // opt-in (default off): pin the horizontal strip's overflow-y to hidden so it never scrolls
  // vertically. Used by carousels whose cards have a drop-shadow that would otherwise trigger a
  // vertical scroll via the overflow-x:scroll → overflow-y:auto browser behaviour.
  clipY?: boolean;
  supressCalculatedHeight?: boolean;
  onSlideIndexChange?: (index: number) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

const SCROLL_KEEPERS = new Map<string | symbol, number>();

function usePersistentScrollKeeper(scrollContainerRef: React.RefObject<HTMLDivElement>, cacheKey: string | symbol) {
  useEffect(() => {
    if (cacheKey) {
      const lastScroll = SCROLL_KEEPERS.get(cacheKey);
      if (scrollContainerRef.current && lastScroll) {
        scrollContainerRef.current.scrollTo(lastScroll, 0);
      }
    }
    return () => {
      if (cacheKey && scrollContainerRef.current) {
        SCROLL_KEEPERS.set(cacheKey, scrollContainerRef.current.scrollLeft);
      }
    };
  }, [ scrollContainerRef, cacheKey, SCROLL_KEEPERS ]);
}

export const ScrollBox: React.FC<ScrollBoxProps> = (props) => {
  const {
    autoWidth,
    children,
    isAnimated,
    cacheKey,
    carouselLayout,
    padding,
    clipY,
    excludedTail: maybeSeeAllButton,
    supressCalculatedHeight,
    onSlideIndexChange,
    onSwipeLeft,
    onSwipeRight,
  } = props;
  const currentScreenBreakpoint = useScreenBreakpoint();
  const scrollSpyInitialized = useRef(false);
  const rootContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  usePersistentScrollKeeper(scrollContainerRef, cacheKey);

  const gestureState = useRef(null);

  const count = React.Children.count(children);
  const trailedCount = Math.min(count, 2);
  const trail = useTrail(trailedCount, {
    config: {
      mass: 1,
      tension: 2000,
      friction: 200,
    },
    delay: 400,
    opacity: 1,
    x: 0,
    from: { opacity: 0, x: 50 },
  });

  let mappedChildren = React.Children.map(children, (child, index) => (
    <ScrollSpyTarget targetId={index}>
      {child}
    </ScrollSpyTarget>
  ));

  if (isAnimated && currentScreenBreakpoint <= 2) {
    const trailedChildren = trail.map(({ x, ...rest }, index) => {
      return (
        <animated.div
          key={index}
          style={{
            ...rest,
            transform: x.to((X: number) => `translate3d(${X}px,0,0)`),
            flex: 1,
          }}
        >
          <ScrollSpyTarget targetId={index}>
            {children[index]}
          </ScrollSpyTarget>
        </animated.div>
      );
    });
    mappedChildren = mappedChildren.map((child, index) => (
      <div key={index}>
        {child}
      </div>
    ));

    mappedChildren = [ ...trailedChildren, ...mappedChildren.slice(trailedCount) ];
  }

  if (maybeSeeAllButton) {
    mappedChildren.push(React.cloneElement(maybeSeeAllButton, { key: 'see-all' }));
  }

  const handleScrollSpyChange = (key: string) => {
    if (scrollSpyInitialized.current) {
      const index = parseInt(key, 10);
      onSlideIndexChange(index);
    }
    scrollSpyInitialized.current = true;
  };

  const panHandlers = createPanResponder({
    onStart: (state) => gestureState.current = state,
    onMove: (state) => gestureState.current = state,
    onEnd: () => {
      const isSideSwipe = gestureState && gestureState.current && Math.abs(gestureState.current.dx) > Math.abs(gestureState.current.dy);
      if (onSwipeLeft && isSideSwipe && gestureState.current.dx > 0) onSwipeLeft();
      else if (onSwipeRight && isSideSwipe && gestureState.current.dx < 0) onSwipeRight();
    },
  });

  return (
    <ScrollSpyProvider
      onChange={handleScrollSpyChange}
      root={rootContainerRef}
    >
      <ScrollXBoxWrapper className="scrollBox-wrapper" ref={rootContainerRef} {...panHandlers}>
        <ScrollXBoxContainer withOverflow={currentScreenBreakpoint <= 2}>
          {autoWidth ? (
            <ScrollXBox ref={scrollContainerRef} clipY={clipY}>
              <ScrollXBoxPaddingContainer
                className="scrollBox"
                padding={padding}
                supressCalculatedHeight={supressCalculatedHeight}
              >
                {mappedChildren}
              </ScrollXBoxPaddingContainer>
            </ScrollXBox>
          ) : (
            <ScrollXBoxWithCalculatedWidth
              ref={scrollContainerRef}
              currentScreenBreakpoint={currentScreenBreakpoint}
              {...(carouselLayout || CAROUSEL_LAYOUT)}
            >
              <ScrollXBoxPaddingContainer
                className="scrollBox"
                padding={padding}
                supressCalculatedHeight={supressCalculatedHeight}
              >
                {mappedChildren}
              </ScrollXBoxPaddingContainer>
            </ScrollXBoxWithCalculatedWidth>
          )}
        </ScrollXBoxContainer>
      </ScrollXBoxWrapper>
    </ScrollSpyProvider>
  );
};

ScrollBox.defaultProps = {
  onSlideIndexChange: noop,
};
